import React from 'react';
import { Search, FilterX, Building2 } from 'lucide-react';
import { AccountingWorkspaceView, ARWorkspaceView } from '../../../api/types';
import FilterSelect from '../components/FilterSelect';

export interface AccountingLedgerFiltersProps {
    view: 'accounting' | 'ar' | 'inventory';
    searchValue: string;
    onSearchChange: (value: string) => void;
    accountingSource: AccountingWorkspaceView;
    onAccountingSourceChange: (value: AccountingWorkspaceView) => void;
    arSource?: ARWorkspaceView;
    onArSourceChange?: (value: ARWorkspaceView) => void;
    statusValue: string;
    onStatusChange: (value: string) => void;
    transactionTypeValue: string;
    onTransactionTypeChange: (value: string) => void;
    recordTypeValue?: string;
    onRecordTypeChange?: (value: string) => void;
    accountNameValue: string;
    onAccountNameChange: (value: string) => void;
    splitAccountNameValue: string;
    onSplitAccountNameChange: (value: string) => void;
    balanceStateValue: string;
    onBalanceStateChange: (value: string) => void;
    priceChangeValue: string;
    onPriceChangeChange: (value: string) => void;
    inventoryCurrencyValue: string;
    onInventoryCurrencyChange: (value: string) => void;
    inventoryVendorValue: string;
    onInventoryVendorChange: (value: string) => void;
    inventoryHistoryValue: string;
    onInventoryHistoryChange: (value: string) => void;
    accountOptions: string[];
    splitAccountOptions: string[];
    inventoryCurrencyOptions: string[];
    inventoryVendorOptions: string[];
    glCodeQboTypeValue?: string;
    onGlCodeQboTypeChange?: (value: string) => void;
    glCodeClassificationValue?: string;
    onGlCodeClassificationChange?: (value: string) => void;
    glCodeFinzCategoryValue?: string;
    onGlCodeFinzCategoryChange?: (value: string) => void;
    glCodeQboTypeOptions?: string[];
    glCodeClassificationOptions?: string[];
    glCodeFinzCategoryOptions?: string[];
    onClearFilters: () => void;
    onOpenQboVendorList: () => void;
    qboDirectoryLabel?: string;
    onOpenArWeeklySummary?: () => void;
    arWeeklySummaryLabel?: string;
    onOpenFinzAccountList?: () => void;
    dueThisWeekValue?: boolean;
    onDueThisWeekChange?: (value: boolean) => void;
    overdueOnlyValue?: boolean;
    onOverdueOnlyChange?: (value: boolean) => void;
    criticalOnlyValue?: boolean;
    onCriticalOnlyChange?: (value: boolean) => void;
    unappliedCashValue?: boolean;
    onUnappliedCashChange?: (value: boolean) => void;
    hideSearch?: boolean;
}

const transactionTypeOptions = [
    'Payment',
    'Deposit',
    'Invoice',
    'Bill',
    'Expense',
    'SalesReceipt',
    'Transfer',
    'Check',
];

const arRecordTypeOptions = [
    'invoice',
    'payment',
];

const apRecordTypeOptions = [
    'bill',
    'bill_line',
    'bill_payment',
    'vendor_credit',
    'document',
    'bank_outflow',
    'obligation',
    'exception',
];

const getApArDomain = (source: ARWorkspaceView): 'ar' | 'ap' => (
    source === 'ap_bills' || source === 'ap_vendors' || source === 'ap_ledger' ? 'ap' : 'ar'
);

const getApArSubview = (source: ARWorkspaceView): 'customers' | 'ledger' | 'vendors' => {
    if (source === 'ar_ledger') return 'ledger';
    if (source === 'ap_vendors') return 'vendors';
    if (source === 'ap_bills' || source === 'ap_ledger') return 'ledger';
    return 'customers';
};

const getArSourceFromControls = (
    domain: 'ar' | 'ap',
    subview: 'customers' | 'ledger' | 'vendors',
): ARWorkspaceView => {
    if (domain === 'ap') return subview === 'vendors' ? 'ap_vendors' : 'ap_ledger';
    return subview === 'ledger' ? 'ar_ledger' : 'ar_customers';
};

const AccountingLedgerFilters: React.FC<AccountingLedgerFiltersProps> = ({
    view,
    searchValue,
    onSearchChange,
    accountingSource,
    onAccountingSourceChange,
    arSource = 'ar_customers',
    onArSourceChange,
    statusValue,
    onStatusChange,
    transactionTypeValue,
    onTransactionTypeChange,
    recordTypeValue = '',
    onRecordTypeChange,
    accountNameValue,
    onAccountNameChange,
    splitAccountNameValue,
    onSplitAccountNameChange,
    balanceStateValue,
    onBalanceStateChange,
    priceChangeValue,
    onPriceChangeChange,
    inventoryCurrencyValue,
    onInventoryCurrencyChange,
    inventoryVendorValue,
    onInventoryVendorChange,
    inventoryHistoryValue,
    onInventoryHistoryChange,
    accountOptions,
    splitAccountOptions,
    inventoryCurrencyOptions,
    inventoryVendorOptions,
    glCodeQboTypeValue = '',
    onGlCodeQboTypeChange,
    glCodeClassificationValue = '',
    onGlCodeClassificationChange,
    glCodeFinzCategoryValue = '',
    onGlCodeFinzCategoryChange,
    glCodeQboTypeOptions = [],
    glCodeClassificationOptions = [],
    glCodeFinzCategoryOptions = [],
    onClearFilters,
    onOpenQboVendorList,
    qboDirectoryLabel = 'QBO Vendors',
    onOpenArWeeklySummary,
    arWeeklySummaryLabel = 'AR Weekly Summary',
    onOpenFinzAccountList,
    dueThisWeekValue = false,
    onDueThisWeekChange,
    overdueOnlyValue = false,
    onOverdueOnlyChange,
    criticalOnlyValue = false,
    onCriticalOnlyChange,
    unappliedCashValue = false,
    onUnappliedCashChange,
    hideSearch = false,
}) => {
    const isArView = view === 'ar';
    const isApSource = isArView && (arSource === 'ap_bills' || arSource === 'ap_vendors' || arSource === 'ap_ledger');
    const isApBillsSource = isArView && arSource === 'ap_bills';
    const isApVendorsSource = isArView && arSource === 'ap_vendors';
    const isArLedgerSource = isArView && arSource === 'ar_ledger';
    const isApLedgerSource = isArView && arSource === 'ap_ledger';
    const apArDomain = getApArDomain(arSource);
    const apArSubview = getApArSubview(arSource);
    const isGlCodeSource = view === 'accounting' && accountingSource === 'gl_code';
    const isArSource = isArView || (view === 'accounting' && accountingSource === 'ar');
    const isUploadedSource = accountingSource === 'uploaded_invoices' || accountingSource === 'uploaded_expenses' || accountingSource === 'uploaded_bills';
    const isUploadedPriceSignalSource = accountingSource === 'uploaded_invoices' || accountingSource === 'uploaded_bills';
    const isInvoiceBillSource = accountingSource !== 'transaction_list' && accountingSource !== 'general_ledger' && accountingSource !== 'gl_code' && accountingSource !== 'ar';
    const isTransactionListSource = view === 'accounting' && accountingSource === 'transaction_list';
    const isGeneralLedgerSource = view === 'accounting' && accountingSource === 'general_ledger';
    const isAccountFilterSource = isTransactionListSource || isGeneralLedgerSource;

    return (
        <div className="py-2 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                {!hideSearch && (
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder={
                                view === 'inventory'
                                    ? 'Search vendor, item, or invoice ID...'
                                    : isGlCodeSource
                                        ? 'Search QBO account, type, classification, or Finz mapping...'
                                        : isArSource
                                            ? isApBillsSource
                                                ? 'Search vendor, bill #, due date, or status...'
                                                : isApVendorsSource
                                                    ? 'Search vendor...'
                                                    : isApLedgerSource
                                                        ? 'Search vendor, reference, record type, or status...'
                                                    : isArLedgerSource
                                                        ? 'Search customer, reference, record type, or status...'
                                                        : 'Search customer...'
                                        : 'Search customer, vendor, memo, amount, document, line item...'
                            }
                            className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            value={searchValue}
                            onChange={(e) => onSearchChange(e.target.value)}
                        />
                    </div>
                )}

                {isGlCodeSource && (
                    <FilterSelect
                        className="min-w-[220px]"
                        value={glCodeQboTypeValue}
                        onChange={(e) => onGlCodeQboTypeChange?.(e.target.value)}
                    >
                        <option value="">QBO Type: All</option>
                        {glCodeQboTypeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isGlCodeSource && (
                    <FilterSelect
                        className="min-w-[220px]"
                        value={glCodeClassificationValue}
                        onChange={(e) => onGlCodeClassificationChange?.(e.target.value)}
                    >
                        <option value="">Classification: All</option>
                        {glCodeClassificationOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isGlCodeSource && (
                    <FilterSelect
                        className="min-w-[220px]"
                        value={glCodeFinzCategoryValue}
                        onChange={(e) => onGlCodeFinzCategoryChange?.(e.target.value)}
                    >
                        <option value="">Finz Category: All</option>
                        {glCodeFinzCategoryOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isTransactionListSource && (
                    <FilterSelect
                        value={transactionTypeValue}
                        onChange={(e) => onTransactionTypeChange(e.target.value)}
                    >
                        <option value="">Type: All</option>
                        {transactionTypeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isGeneralLedgerSource && (
                    <FilterSelect
                        value={transactionTypeValue}
                        onChange={(e) => onTransactionTypeChange(e.target.value)}
                    >
                        <option value="">Type: All</option>
                        {transactionTypeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isAccountFilterSource && (
                    <FilterSelect
                        className="w-40"
                        value={accountNameValue}
                        onChange={(e) => onAccountNameChange(e.target.value)}
                    >
                        <option value="">Account: All</option>
                        {accountOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {isAccountFilterSource && (
                    <FilterSelect
                        className="w-40"
                        value={splitAccountNameValue}
                        onChange={(e) => onSplitAccountNameChange(e.target.value)}
                    >
                        <option value="">Split Account: All</option>
                        {splitAccountOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {((view === 'accounting' && isInvoiceBillSource) || isArLedgerSource || isApBillsSource) && (
                    <FilterSelect
                        className="min-w-[180px]"
                        value={balanceStateValue}
                        onChange={(e) => onBalanceStateChange(e.target.value)}
                    >
                        <option value="">Balance: All</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </FilterSelect>
                )}

                {(isArLedgerSource || isApLedgerSource) && (
                    <FilterSelect
                        className="min-w-[180px]"
                        value={recordTypeValue}
                        onChange={(e) => onRecordTypeChange?.(e.target.value)}
                    >
                        <option value="">Record Type: All</option>
                        {(isApLedgerSource ? apRecordTypeOptions : arRecordTypeOptions).map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {view === 'accounting' && isUploadedSource && (
                    <FilterSelect
                        value={statusValue}
                        onChange={(e) => onStatusChange(e.target.value)}
                    >
                        <option value="">Status: All</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="posted_to_qbo">Posted to QBO</option>
                    </FilterSelect>
                )}

                {isApBillsSource && (
                    <FilterSelect
                        value={statusValue}
                        onChange={(e) => onStatusChange(e.target.value)}
                    >
                        <option value="">Status: All</option>
                        <option value="open">Open</option>
                        <option value="due_this_week">Due This Week</option>
                        <option value="overdue">Overdue</option>
                        <option value="paid">Paid</option>
                    </FilterSelect>
                )}

                {view === 'accounting' && isUploadedPriceSignalSource && (
                    <FilterSelect
                        value={priceChangeValue}
                        onChange={(e) => onPriceChangeChange(e.target.value)}
                    >
                        <option value="">Price Change: All</option>
                        <option value="changed">Has Changes</option>
                        <option value="unchanged">No Changes</option>
                    </FilterSelect>
                )}

                {view === 'inventory' && (
                    <FilterSelect
                        value={inventoryVendorValue}
                        onChange={(e) => onInventoryVendorChange(e.target.value)}
                    >
                        <option value="">Vendor: All</option>
                        {inventoryVendorOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )}

                {/* {view === 'inventory' && (
                    <FilterSelect
                        value={inventoryCurrencyValue}
                        onChange={(e) => onInventoryCurrencyChange(e.target.value)}
                    >
                        <option value="">Currency: All</option>
                        {inventoryCurrencyOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </FilterSelect>
                )} */}

                {view === 'inventory' && (
                    <FilterSelect
                        value={inventoryHistoryValue}
                        onChange={(e) => onInventoryHistoryChange(e.target.value)}
                    >
                        <option value="">History: All</option>
                        <option value="repeat">Repeat Items</option>
                        <option value="single">Single Observation</option>
                        <option value="changed">Changed Price</option>
                        <option value="stable">Stable Price</option>
                    </FilterSelect>
                )}

                {view === 'accounting' && (
                    <FilterSelect
                        className="ml-auto min-w-[220px]"
                        value={accountingSource}
                        onChange={(e) => onAccountingSourceChange(e.target.value as AccountingWorkspaceView)}
                    >
                        <option value="transaction_list">Transaction List</option>
                        {/* <option value="general_ledger">General Ledger</option> */}
                        <option value="invoices">Invoices (QBO)</option>
                        <option value="bills">Bills (QBO)</option>
                        <option value="uploaded_invoices">Invoices (Upload)</option>
                        <option value="uploaded_expenses">Expenses (Upload)</option>
                        <option value="uploaded_bills">Bills (Upload)</option>
                        <option value="gl_code">GL Code Mapping</option>
                    </FilterSelect>
                )}

                {isArLedgerSource && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onDueThisWeekChange?.(!dueThisWeekValue)}
                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${dueThisWeekValue
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            Due This Week
                        </button>
                        <button
                            onClick={() => onUnappliedCashChange?.(!unappliedCashValue)}
                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${unappliedCashValue
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            Unapplied Cash
                        </button>
                    </div>
                )}

                {isApBillsSource && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onDueThisWeekChange?.(!dueThisWeekValue)}
                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${dueThisWeekValue
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            Due This Week
                        </button>
                        <button
                            onClick={() => onOverdueOnlyChange?.(!overdueOnlyValue)}
                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${overdueOnlyValue
                                    ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            Overdue
                        </button>
                    </div>
                )}

                {view === 'ar' && (
                    <div className="ml-auto flex items-center gap-3">
                        <FilterSelect
                            className="min-w-[150px]"
                            value={apArDomain}
                            onChange={(e) => {
                                const nextDomain = e.target.value as 'ar' | 'ap';
                                const nextSubview = nextDomain === 'ap'
                                    ? (apArSubview === 'ledger' || apArSubview === 'customers' ? 'vendors' : apArSubview)
                                    : (apArSubview === 'vendors' ? 'customers' : apArSubview);
                                onArSourceChange?.(getArSourceFromControls(nextDomain, nextSubview));
                            }}
                        >
                            <option value="ar">AR</option>
                            <option value="ap">AP</option>
                        </FilterSelect>
                        <FilterSelect
                            className="min-w-[170px]"
                            value={apArSubview}
                            onChange={(e) => {
                                const nextSubview = e.target.value as 'customers' | 'ledger' | 'vendors';
                                onArSourceChange?.(getArSourceFromControls(apArDomain, nextSubview));
                            }}
                        >
                            {apArDomain === 'ar' ? (
                                <>
                                    <option value="customers">Customers</option>
                                    <option value="ledger">Ledger</option>
                                </>
                            ) : (
                                <>
                                    <option value="vendors">Vendors</option>
                                    <option value="ledger">Ledger</option>
                                </>
                            )}
                        </FilterSelect>
                    </div>
                )}

            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onClearFilters}
                        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                    >
                        <FilterX className="h-4 w-4" />
                        Clear Filter
                    </button>
                </div>

                {view === 'accounting' && !isGlCodeSource && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onOpenQboVendorList}
                            className="flex items-center gap-2 text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-2.5 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                            <Building2 className="h-4 w-4" />
                            {qboDirectoryLabel}
                        </button>
                    </div>
                )}

                {view === 'ar' && (
                    <div className="flex items-center gap-2">
                        <button
                            data-tutorial="ledger-ar-weekly-summary-button"
                            onClick={onOpenArWeeklySummary}
                            className="flex items-center gap-2 text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-2.5 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                            <Building2 className="h-4 w-4" />
                            {arWeeklySummaryLabel}
                        </button>
                    </div>
                )}

                {view === 'accounting' && isGlCodeSource && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onOpenFinzAccountList}
                            className="flex items-center gap-2 text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-2.5 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                            <Building2 className="h-4 w-4" />
                            Finz Accounts
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountingLedgerFilters;
